import { find, flow, get, has, isArray, isEmpty, reduce, sortBy } from 'lodash'
import { createSelector } from 'reselect'

import { DEFAULT_PERMISSIONS, USER_ADMIN_ROLES } from 'src/constants'
import {
  APPOINTMENT_CALLBACK_FIELDS_MODE,
  APPOINTMENT_CALLBACK_FIELDS_MODE_ENUM,
  IS_ALLOW_SIMPLE_UI_FOR_URGENT_CARE_CAREGIVERS,
  PATIENT_REQUESTED_APPOINTMENT_ALLOWED
} from 'src/scenes/Companies/scenes/CompaniesSearch/constants'
import { moveItem } from 'src/utils/Array'

export const user$ = (state) => state.user
export const userProfile$ = createSelector(user$, (user) => user.profile)
export const userUuid$ = createSelector(userProfile$, (user) => user.uuid)
export const userIsSupportUser$ = createSelector(
  userProfile$,
  (user) => user?.isSupportUser || false
)

export const isAuthenticated$ = createSelector(
  user$,
  (user) => user.isAuthenticated
)
export const isManuallyLoggedOut$ = createSelector(
  user$,
  (user) => user.isManuallyLoggedOut
)
export const isRequiredUserDataLoaded$ = createSelector(
  user$,
  (user) => user.isRequiredUserDataLoaded
)
export const userName$ = createSelector(
  userProfile$,
  ({ fullName }) => fullName
)

export const userCompanyUuid$ = createSelector(
  userProfile$,
  ({ companyUuid = null }) => companyUuid
)

export const isUrgentCareLoadBalancing$ = createSelector(
  userProfile$,
  ({ isUrgentCareLoadBalancing = false }) => isUrgentCareLoadBalancing
)

export const userCompanyLanguageTitle$ = createSelector(
  userProfile$,
  (profile) => get(profile, 'companyLanguageTitle', '')
)

export const userCompanyLanguage$ = createSelector(
  userProfile$,
  userCompanyLanguageTitle$,
  (profile, languageTitle) =>
    flow(
      (prof) => get(prof, 'companyLanguages', []),
      (langs) => find(langs, ({ title }) => title === languageTitle)
    )(profile)
)

export const userIsBillingRequired$ = createSelector(userProfile$, (profile) =>
  get(profile, 'isBillingRequired', false)
)

export const isStartTimerOnChartReview$ = createSelector(
  userProfile$,
  (profile) => Boolean(get(profile, 'isStartTimerOnChartReview', false))
)

export const userCreated$ = createSelector(userProfile$, (profile) =>
  get(profile, 'companyCreatedAt')
)

export const checkSecuredEmailPassword$ = createSelector(
  userProfile$,
  (profile) => get(profile, 'checkSecuredEmailPassword')
)

export const isMarketerDailyReportEnabled$ = createSelector(
  userProfile$,
  (profile) => get(profile, 'isMarketerDailyReportEnabled', false)
)

export const userCompanyLanguagesSelector = createSelector(
  userProfile$,
  (profile) => {
    let companyLanguages = get(profile, 'companyLanguages', [])
    const englishIndex = companyLanguages.findIndex(
      (item) => item.title === 'English'
    )

    companyLanguages = moveItem(companyLanguages, englishIndex)

    return companyLanguages.map((item) => ({
      ...item,
      disabled: !item.enabled || false
    }))
  }
)

export const userCompanyEnglishLanguage$ = createSelector(
  userProfile$,
  (profile) =>
    flow(
      (prof) => get(prof, 'companyLanguages', []),
      (langs) => find(langs, ({ title }) => title === 'English'),
      (langs) => get(langs, 'uuid', '')
    )(profile)
)

export const companyLanguage$ = createSelector(
  userProfile$,
  ({ companyLanguageUuid = '' }) => companyLanguageUuid
)

export const userLicenseModule$ = createSelector(userProfile$, (profile) =>
  get(profile, 'licenseModule', [])
)

export const userTimeZone$ = createSelector(userProfile$, (profile) =>
  get(profile, 'companyTimezone', '')
)

export const companyTitle$ = createSelector(userProfile$, (profile) =>
  get(profile, 'companyTitle', '')
)

export const isAppointmentOutcomeNoteRequired$ = createSelector(
  userProfile$,
  (profile) => profile?.isAppointmentOutcomeNoteRequired || false
)

export const emrCareGiverManagement$ = createSelector(
  userProfile$,
  (profile) => profile?.emrCareGiverManagement || false
)

const getAppointmentCallbackFieldsMode = (profile = {}) => {
  const appointmentCallbackFieldsMode =
    profile?.[APPOINTMENT_CALLBACK_FIELDS_MODE]

  if (typeof appointmentCallbackFieldsMode === 'number') {
    return appointmentCallbackFieldsMode
  }

  return profile?.isAppointmentCallbackFieldsRequired
    ? APPOINTMENT_CALLBACK_FIELDS_MODE_ENUM.REQUIRED
    : APPOINTMENT_CALLBACK_FIELDS_MODE_ENUM.HIDDEN
}

export const appointmentCallbackFieldsMode$ = createSelector(
  userProfile$,
  (profile = {}) => getAppointmentCallbackFieldsMode(profile)
)

export const isAppointmentCallbackFieldsVisible$ = createSelector(
  appointmentCallbackFieldsMode$,
  (appointmentCallbackFieldsMode) =>
    appointmentCallbackFieldsMode !==
    APPOINTMENT_CALLBACK_FIELDS_MODE_ENUM.HIDDEN
)

export const isAppointmentCallbackFieldsRequiredMode$ = createSelector(
  appointmentCallbackFieldsMode$,
  (appointmentCallbackFieldsMode) =>
    appointmentCallbackFieldsMode ===
    APPOINTMENT_CALLBACK_FIELDS_MODE_ENUM.REQUIRED
)

export const isAppointmentCallbackFieldsRequired$ = createSelector(
  isAppointmentCallbackFieldsVisible$,
  (isAppointmentCallbackFieldsVisible) => isAppointmentCallbackFieldsVisible
)

export const userRole$ = createSelector(
  user$,
  (user) => Object.keys(get(user, 'roles', {}))[0]
)

export const userCompanyDivision$ = createSelector(
  userProfile$,
  ({ companyDivision }) => companyDivision
)

export const userCompanyDivisionUuid$ = createSelector(
  userCompanyDivision$,
  (companyDivision = {}) => companyDivision?.uuid
)

export const isReferralSourceRequired$ = createSelector(
  userProfile$,
  (userProfile = {}) => get(userProfile, ['isReferralSourceRequired'], false)
)

export const companyProviders$ = createSelector(userProfile$, ({ providers }) =>
  sortBy(providers, ({ name }) => name.toLowerCase())
)

export const companyActiveProviders$ = createSelector(
  companyProviders$,
  (providers) => providers.filter(({ isActive }) => isActive)
)

export const companyActiveProvidersDropdown$ = createSelector(
  companyActiveProviders$,
  (companyProviders = []) =>
    companyProviders.map(({ name, uuid }) => ({
      key: uuid,
      title: name,
      value: uuid
    }))
)

export const companyProvidersDropdown$ = createSelector(
  companyProviders$,
  (providers) =>
    providers.map((item, key) => ({
      ...item,
      key,
      title: item.name,
      value: item.uuid
    }))
)

export const isTeleMedicineBillable$ = createSelector(
  userProfile$,
  ({ isTeleMedicineBillable }) => isTeleMedicineBillable
)

export const canCreateG0506FromAppointment$ = createSelector(
  userProfile$,
  ({ canCreateG0506FromAppointment }) => canCreateG0506FromAppointment
)

const DEFAULT_G0506_ACTIVITY_LOG_DESCRIPTION =
  "At the time of the E&M evaluation with the provider, a care plan was created for the patient's chronic & behavioral conditions that will be monitored on an ongoing monthly basis. CCM and BHI programs will be utilized to improve patient care and outcomes."

export const g0506ActivityLogDescription$ = createSelector(
  userProfile$,
  (profile = {}) =>
    get(profile, ['g0506ActivityLogDescription']) ||
    DEFAULT_G0506_ACTIVITY_LOG_DESCRIPTION
)

const ORDER_MANAGEMENT_PERMISSION_FALLBACKS = {
  patientDurableMedicalEquipmentOrderManagement: ['medicalDevicesAndOrders'],
  patientPrescriptionManagement: ['medication', 'medicationManagement'],
  patientExternalReferralManagement: ['referralManagement'],
  // Temporary compatibility fallback until backend returns patientDiagnosticOrderManagement
  patientDiagnosticOrderManagement: ['diagnosis']
}

const CHART_REVIEW_DEPENDENT_PERMISSIONS = [
  'diagnosis',
  'enrolledProgram',
  'pastSurgicalHistory',
  'socialHistory',
  'patientDemographics',
  'medicationManagement',
  'rpmDeviceInfo',
  'measurementManagement',
  'patientAssessment',
  'activityLog'
]

const getPrimaryRolePermissions = (roles = {}) => {
  const primaryRoleKey = Object.keys(roles || {})[0]

  if (!primaryRoleKey) return {}

  return roles?.[primaryRoleKey] || {}
}

const getPermissionWithFallback = (
  rolePermissions = {},
  permissionName = ''
) => {
  const directPermissions = get(rolePermissions, [
    permissionName,
    'permissions'
  ])

  if (directPermissions) {
    return directPermissions
  }

  const fallbackPermissionNames =
    ORDER_MANAGEMENT_PERMISSION_FALLBACKS?.[permissionName] || []

  if (!fallbackPermissionNames.length) {
    return DEFAULT_PERMISSIONS
  }

  let hasFallbackPermission = false
  let read = false
  let write = false
  let none = true
  let hasNoneField = false

  fallbackPermissionNames.forEach((fallbackPermissionName) => {
    const permissions = get(rolePermissions, [
      fallbackPermissionName,
      'permissions'
    ])

    if (!permissions) return

    hasFallbackPermission = true

    // Order management pages allow access when write exists even if read is absent.
    read =
      read ||
      get(permissions, ['read'], false) ||
      get(permissions, ['write'], false)
    write = write || get(permissions, ['write'], false)

    if ('none' in permissions) {
      hasNoneField = true
      none = none && permissions.none === true
    }
  })

  if (!hasFallbackPermission) {
    return DEFAULT_PERMISSIONS
  }

  return {
    read,
    write,
    none: hasNoneField ? none : false
  }
}

const withPermissionFallbacks = (rolePermissions = {}) => {
  if (!rolePermissions || typeof rolePermissions !== 'object') {
    return rolePermissions
  }

  let nextPermissions = rolePermissions

  Object.keys(ORDER_MANAGEMENT_PERMISSION_FALLBACKS).forEach(
    (permissionName) => {
      const directPermissions = get(rolePermissions, [
        permissionName,
        'permissions'
      ])

      if (directPermissions) return

      const fallbackPermissions = getPermissionWithFallback(
        rolePermissions,
        permissionName
      )

      if (fallbackPermissions === DEFAULT_PERMISSIONS) return

      if (nextPermissions === rolePermissions) {
        nextPermissions = { ...rolePermissions }
      }

      nextPermissions[permissionName] = {
        permissions: fallbackPermissions
      }
    }
  )

  return nextPermissions
}

const withWriteImplyingRead = (rolePermissions = {}) => {
  if (!rolePermissions || typeof rolePermissions !== 'object') {
    return rolePermissions
  }

  let nextPermissions = rolePermissions

  Object.keys(rolePermissions).forEach((permissionName) => {
    const permissions = get(rolePermissions, [permissionName, 'permissions'])

    if (!permissions?.write || permissions?.read) {
      return
    }

    if (nextPermissions === rolePermissions) {
      nextPermissions = { ...rolePermissions }
    }

    nextPermissions[permissionName] = {
      ...rolePermissions[permissionName],
      permissions: {
        ...permissions,
        read: true
      }
    }
  })

  return nextPermissions
}

const withChartReviewPermissionDependencies = (rolePermissions = {}) => {
  if (!rolePermissions || typeof rolePermissions !== 'object') {
    return rolePermissions
  }

  const chartReviewPermissions = getPermissionWithFallback(
    rolePermissions,
    'chartReview'
  )

  if (!get(chartReviewPermissions, ['write'], false)) {
    return rolePermissions
  }

  let nextPermissions = rolePermissions

  CHART_REVIEW_DEPENDENT_PERMISSIONS.forEach((permissionName) => {
    const currentPermissions = getPermissionWithFallback(
      rolePermissions,
      permissionName
    )

    if (nextPermissions === rolePermissions) {
      nextPermissions = { ...rolePermissions }
    }

    nextPermissions[permissionName] = {
      ...nextPermissions[permissionName],
      permissions: {
        ...currentPermissions,
        read: true,
        write: true,
        none: false
      }
    }
  })

  return nextPermissions
}

const getNormalizedRolePermissions = (roles = {}) =>
  withChartReviewPermissionDependencies(
    withWriteImplyingRead(
      withPermissionFallbacks(getPrimaryRolePermissions(roles))
    )
  )

export const userPermissionByName$ = (permissionName) =>
  createSelector(userProfile$, ({ roles }) => {
    if (!roles) return DEFAULT_PERMISSIONS

    const rolePermissions = getNormalizedRolePermissions(roles)

    return getPermissionWithFallback(rolePermissions, permissionName)
  })

export const userPermissionByNameIsNone$ = (permissionName = '') =>
  createSelector(userProfile$, ({ roles = {} }) => {
    const permissions = getPermissionWithFallback(
      getNormalizedRolePermissions(roles),
      permissionName
    )

    return 'none' in permissions && permissions?.none === true
  })

export const userPermissionByNameIsRead$ = (permissionName = '') =>
  createSelector(userProfile$, ({ roles = {} }) => {
    const permissions = getPermissionWithFallback(
      getNormalizedRolePermissions(roles),
      permissionName
    )
    let isRead = !get(permissions, ['none'], false)

    if (isRead) {
      isRead = has(permissions, 'read')
        ? get(permissions, ['read'], false)
        : get(permissions, ['write'], false)
    }

    return isRead
  })

export const userPermissionByNameIsWrite$ = (permissionName = '') =>
  createSelector(userProfile$, ({ roles = {} }) => {
    const permissions = getPermissionWithFallback(
      getNormalizedRolePermissions(roles),
      permissionName
    )
    let isWrite = !get(permissions, ['none'], false)

    if (isWrite) {
      isWrite = get(permissions, ['write'], false)
    }

    return isWrite
  })

export const userPermissions$ = createSelector(userProfile$, ({ roles }) => {
  if (!roles) return ''

  return getNormalizedRolePermissions(roles)
})

export const isBillable$ = createSelector(
  userProfile$,
  ({ isBillable }) => isBillable
)

export const caseManagerNotificationsAlertsFilter$ = createSelector(
  userProfile$,
  ({ caseManagerNotifications }) =>
    !isArray(caseManagerNotifications)
      ? reduce(
          caseManagerNotifications,
          (result, item) => {
            result.push(item)

            return result
          },
          []
        )
      : caseManagerNotifications.map(({ uuid }) => uuid)
)

export const isPatientCompanyGamificationEnabled$ = createSelector(
  userProfile$,
  ({ isPatientCompanyGamificationEnabled = false }) =>
    isPatientCompanyGamificationEnabled
)

export const isCareGiverCompanyGamificationEnabled$ = createSelector(
  userProfile$,
  ({ isCareGiverCompanyGamificationEnabled = false }) =>
    isCareGiverCompanyGamificationEnabled
)

export const isEnabledActivityLogFacilityNotification$ = createSelector(
  userProfile$,
  ({ isEnabledActivityLogFacilityNotification = false }) =>
    isEnabledActivityLogFacilityNotification
)

export const isUrgentCareUser$ = createSelector(
  user$,
  ({ isUrgentCareUser }) => isUrgentCareUser
)

export const isFacilityManagementEnabled$ = createSelector(
  userPermissions$,
  (userPermissions = {}) =>
    get(
      userPermissions,
      ['facilityManagement', 'permissions', 'write'],
      DEFAULT_PERMISSIONS.write
    )
)

export const rememberPasswordExpiration$ = createSelector(
  user$,
  ({ rememberPasswordExpiration }) => rememberPasswordExpiration
)

export const shouldShowPasswordExpired$ = createSelector(
  user$,
  ({ shouldShowPasswordExpired }) => shouldShowPasswordExpired
)

export const ribbonTimestamp$ = createSelector(
  user$,
  ({ ribbonTimestamp }) => ribbonTimestamp
)

export const isReferralSourceManagementEnabled$ = createSelector(
  userPermissions$,
  (userPermissions = {}) =>
    get(
      userPermissions,
      ['referralSourceManagement', 'permissions', 'write'],
      DEFAULT_PERMISSIONS.write
    )
)

export const isReferralImportEnabled$ = createSelector(
  userPermissions$,
  (userPermissions) => userPermissions?.referralImport || false
)

export const companyImportSettings$ = createSelector(
  userProfile$,
  ({ companyImportSettings = {} }) => companyImportSettings
)

export const userIsNextCallDateRequired$ = createSelector(
  userProfile$,
  (profile = {}) => get(profile, ['isNextCallDateRequired'], false)
)

export const userActivitiesCounters$ = createSelector(user$, (user = {}) =>
  get(user, ['userActivitiesCounters'], {})
)

export const isUserAdmin$ = createSelector(userRole$, (userRole = '') =>
  USER_ADMIN_ROLES.includes(userRole)
)

export const defaultClinician$ = createSelector(userProfile$, (profile = {}) =>
  get(profile, ['defaultClinicians'], null)
)

export const appointmentStartTime$ = createSelector(
  userProfile$,
  (profile = {}) => get(profile, ['appointmentStartTime'], null)
)

export const isTaskSuperManager$ = createSelector(
  userProfile$,
  (profile = {}) => get(profile, ['isTaskSuperManager'], false)
)

export const appointmentOverlappingType$ = createSelector(
  userProfile$,
  (profile = {}) => get(profile, ['appointmentOverlappingType'], false)
)

export const isFacilityRequired$ = createSelector(
  userProfile$,
  (userProfile = {}) => get(userProfile, ['isFacilityRequired'], false)
)

export const isTaskDueDateRequired$ = createSelector(
  userProfile$,
  (userProfile = {}) => get(userProfile, ['isTaskDueDateRequired'], false)
)

export const defaultAppointmentPage$ = createSelector(
  userProfile$,
  (userProfile = {}) => get(userProfile, ['defaultAppointmentPage'], 1)
)

export const isStartTimerOnAlertAction$ = createSelector(
  userProfile$,
  (userProfile = {}) => get(userProfile, ['isStartTimerOnAlertAction'], true)
)

export const defaultBillingProgram$ = createSelector(
  userProfile$,
  (userProfile = {}) => get(userProfile, ['defaultBillingProgram', 0], null)
)

export const userCreationType$ = createSelector(
  userProfile$,
  (userProfile = {}) => get(userProfile, ['userCreationType'])
)

export const defaultClinicianCalendar$ = createSelector(
  userProfile$,
  (userProfile = {}) => get(userProfile, ['defaultClinicianCalendar', 'days'])
)

export const isShipBobAvailable$ = createSelector(
  userProfile$,
  (userProfile = {}) => get(userProfile, ['isShipBobAvailable'], false)
)

export const isUrgentCareOutcomeNoteRequired$ = createSelector(
  userProfile$,
  ({ requireUrgentCareOutcomeNote = false }) => requireUrgentCareOutcomeNote
)

export const isUrgentCareOutcomeNotesBlocking$ = createSelector(
  userProfile$,
  ({ isUrgentCareOutcomeNotesBlocking = false, companyUuid }) =>
    isUrgentCareOutcomeNotesBlocking
)

export const isShowUrgentCareOutcomeNote$ = createSelector(
  userProfile$,
  ({ showUrgentCareOutcomeNote = false }) => showUrgentCareOutcomeNote
)

export const isUrgentCareTriageNurse$ = createSelector(
  userProfile$,
  ({ isUrgentCareTriageNurse = false }) => isUrgentCareTriageNurse
)

export const isShowRPMBillable$ = createSelector(
  userProfile$,
  ({ isShowRpmBillable = false }) => isShowRpmBillable
)

export const isAskToReportClinicalEvent$ = createSelector(
  userProfile$,
  ({ isAskToReportClinicalEvent }) => isAskToReportClinicalEvent
)

export const userIsShowRpmBillable$ = createSelector(
  userProfile$,
  (profile) => profile?.isShowRpmBillable || false
)

export const defaultCaseManagers$ = createSelector(
  userProfile$,
  ({ caseManagerNotifications = [] }) => caseManagerNotifications
)

export const isInterventionRequiredForCcmActivities$ = createSelector(
  userProfile$,
  ({ isInterventionRequiredForCcmActivities = false }) =>
    isInterventionRequiredForCcmActivities
)

export const isInterventionRequiredForRpmActivities$ = createSelector(
  userProfile$,
  ({ isInterventionRequiredForRpmActivities = false }) =>
    isInterventionRequiredForRpmActivities
)

export const isInterventionRequiredForRtmActivities$ = createSelector(
  userProfile$,
  ({ isInterventionRequiredForRtmActivities = false }) =>
    isInterventionRequiredForRtmActivities
)

export const isInterventionRequiredForBhiActivities$ = createSelector(
  userProfile$,
  ({ isInterventionRequiredForBhiActivities = false }) =>
    isInterventionRequiredForBhiActivities
)

export const isInterventionRequiredForApcmActivities$ = createSelector(
  userProfile$,
  ({ isInterventionRequiredForApcmActivities = false }) =>
    isInterventionRequiredForApcmActivities
)

export const gridSettings$ = createSelector(
  userProfile$,
  ({ gridSettings = {} }) => gridSettings || null
)

export const defaultGridSettings$ = createSelector(
  userProfile$,
  ({ defaultGridSettings = {} }) => defaultGridSettings || null
)

export const isSetConsentDateWithFirstActivity$ = createSelector(
  userProfile$,
  ({ isSetConsentDateWithFirstActivity = false }) =>
    isSetConsentDateWithFirstActivity
)

export const gridSettingsByName$ = (name = '') =>
  createSelector(
    gridSettings$,
    defaultGridSettings$,
    (gridSettings = {}, defaultGridSettings = {}) => {
      const savedSettings = gridSettings?.[name] || null
      const defaultSettings = defaultGridSettings?.[name] || null

      if (name === 'monitoringDashboard' && !savedSettings) {
        return { columns: ['G0506'], filters: {} }
      }

      const columns = !isEmpty(savedSettings?.columns)
        ? savedSettings.columns
        : defaultSettings?.columns
      const filters = !isEmpty(savedSettings?.filters)
        ? savedSettings.filters
        : defaultSettings?.filters
      const sort = !isEmpty(savedSettings?.sort)
        ? savedSettings.sort
        : defaultSettings?.sort

      return {
        columns,
        filters,
        sort,
        hasPersonalFilters: !isEmpty(savedSettings?.filters)
      }
    }
  )

export const userPatientCustomFields$ = createSelector(
  userProfile$,
  ({ patientCustomFields = [] }) => patientCustomFields
)

export const isAllowedActionPoints$ = createSelector(
  userProfile$,
  ({ isAllowedActionPoints = false }) => isAllowedActionPoints
)

export const companyCanHaveNonBillableMeasurements$ = createSelector(
  userProfile$,
  (profile) => profile?.companyCanHaveNonBillableMeasurements || false
)

export const companyType$ = createSelector(
  userProfile$,
  (profile) => profile?.companyType
)

export const companyIsAllowSimpleUiForUrgentCareCaregivers$ = createSelector(
  userProfile$,
  (profile) => profile?.[IS_ALLOW_SIMPLE_UI_FOR_URGENT_CARE_CAREGIVERS]
)

export const companyIsNonBillableActivityLog$ = createSelector(
  userProfile$,
  ({ isNonBillableActivityLog = false }) => isNonBillableActivityLog
)

export const companyIsUrgentCarePopupNotificationSound$ = createSelector(
  userProfile$,
  ({ isUrgentCarePopupNotificationSound = false }) =>
    isUrgentCarePopupNotificationSound
)

export const companyPatientRequestedAppointmentAllowed$ = createSelector(
  userProfile$,
  (profile) => profile?.[PATIENT_REQUESTED_APPOINTMENT_ALLOWED] || false
)

export const userReloadPage$ = createSelector(
  user$,
  (user = {}) => user?.reloadPage || false
)

export const companyImportSettingsReferralConvergenceRate$ = createSelector(
  companyImportSettings$,
  (companyImportSettings = {}) =>
    companyImportSettings?.referralConvergenceRate || {}
)

export const companyImportSettingsCallBackName$ = createSelector(
  companyImportSettings$,
  (companyImportSettings = {}) => companyImportSettings?.callBackName || null
)

export const companyImportSettingsCallBackNumber$ = createSelector(
  companyImportSettings$,
  (companyImportSettings = {}) => companyImportSettings?.callBackNumber || null
)

export const userCanEditReferralLinkedPatient$ = createSelector(
  userProfile$,
  (user = {}) => user?.canEditReferralLinkedPatient || false
)

export const userAppointmentType$ = createSelector(
  userProfile$,
  (profile) => profile?.appointmentType || null
)

export const userIsEnabledActivityLogCareGap$ = createSelector(
  userProfile$,
  (profile) => profile?.isEnabledActivityLogCareGap || false
)

export const appointmentStatusFilterDefaultValue$ = createSelector(
  userProfile$,
  (profile) => profile?.appointmentStatusFilterDefaultValue
)

export const isForceCallBackgroundImage$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isForceCallBackgroundImage || false
)

export const isManualVideoCallAllowed$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isManualVideoCallAllowed
)

export const advanceDirectiveEnabled$ = createSelector(
  userProfile$,
  (user) => user.advanceDirectiveContent
)

export const pccEnabled$ = createSelector(
  userProfile$,
  (user) => user.pccEnabled
)

export const isUCRiskEnabled$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isUCRiskEnabled || false
)

export const isActivityLogAiHelperEnabled$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isActivityLogAiHelperEnabled || false
)

export const isMeetingRiskEnabled$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isMeetingRiskEnabled || false
)

export const isAlertAISummaryEnabled$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isAlertAISummaryEnabled || false
)

export const activityLogPrompts$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.activityLogPrompts || {}
)

export const carePlanSummaryPrompts$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.carePlanSummaryPrompts || {}
)

export const narrativePrompts$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.narrativePrompts || []
)

export const SOAPOverallCommentPrompts$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.SOAPOverallCommentPrompts || {}
)

export const isAIHelperCopyEnabled$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isAIHelperCopyEnabled || false
)

// Profile flag is the company toggle and both prompt ARNs. Also requires write.
export const isPreCallBriefEnabled$ = createSelector(
  userProfile$,
  userPermissions$,
  (profile = {}, userPermissions = {}) =>
    Boolean(profile?.isPreCallBriefEnabled) &&
    get(userPermissions, ['preCallBrief', 'permissions', 'write'], false)
)

export const isMeetingRecordingEnabled$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isMeetingRecordingEnabled || false
)

export const isUCRecordingEnabled$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isUCRecordingEnabled || false
)

export const isAIRiskFactorReadEnabled$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isAIRiskFactorReadEnabled || false
)

export const isAIRiskFactorGenerateEnabled$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isAIRiskFactorGenerateEnabled || false
)

export const isAITranscriptionReadEnabled$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isAITranscriptionReadEnabled || false
)

export const isEnableFacilityCallerRole$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isEnableFacilityCallerRole || false
)

export const lockPatientWithDraftVisit$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.lockPatientWithDraftVisit || false
)

export const isDeleteSoapVisitEnabled$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isDeleteSoapVisitEnabled || false
)

export const isSoapSignedToDraftEnabled$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isSoapSignedToDraftEnabled || false
)
